// Signals — what Firecrawl found on the open web about companies in our
// industries (web_signals). One Firecrawl search monitor per active industry
// re-runs a few queries daily, keeps only never-seen pages, and a judge
// decides whether each is a lower-middle-market business for sale, changing
// hands, or just acquired. Rows Atlas could match are linked; rows it could
// not are the discovery feed — "Create company" turns one into a lead with
// lead source "Web signal".
//
// Work-queue semantics like Buyers → Changes: New is the default tab, every
// action moves the row out of it, Reopen brings it back.

const SIG_TYPE_LABEL = {
  for_sale: 'For sale', acquired: 'Acquired', ownership_change: 'Ownership change',
  succession: 'Succession', expansion: 'Expansion', distress: 'Distress', other: 'Signal',
};
const SIG_TYPE_HUE = {
  for_sale: 'green', acquired: 'blue', ownership_change: 'violet', succession: 'amber',
  expansion: 'teal', distress: 'rose', other: 'slate',
};
const SIG_STATUS_TABS = [
  ['new', 'New'], ['reviewed', 'Reviewed'], ['converted', 'Converted'], ['dismissed', 'Dismissed'], ['all', 'All'],
];
const WEB_LIMIT = 300;      // rows the Web tab asks for; past it the filters narrow the list

// A row the Prospecting pipeline can take (app/signal_prospecting.eligible):
// a company Atlas does not know, named, not an acquisition (those go to the
// acquisition queue on their own), still new.
const canProspectRow = (s) => s && s.status === 'new' && !s.company_id && !!s.company_name
  && s.event_type !== 'acquired' && s.event_type !== 'ownership_change';

const sigAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  const d = Math.round(mins / 60 / 24);
  return d < 14 ? `${d}d ago` : new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// ── Phone ──────────────────────────────────────────────────────────────────
// The phone shell mounts a fresh page on the way Back from a company or deal
// opened here. So on a phone the page leaves its tab, filters, loaded rows and
// scroll behind as it goes (_sigLeft) and takes them back once, when Back
// lands on the same hash for the same acting user. Signals tapped in More
// starts fresh. SigKeep hands the tabs { resume, keep }: what to start from,
// and where to write the state they leave behind. Desktop provides neither.
let _sigLeft = null;
const SigKeep = React.createContext(null);
const sigWho = () => { try { return localStorage.getItem('atlas.activeUserId') || ''; } catch (_) { return ''; } };
// Sheet controls: 16px text (no iOS zoom on focus), 44px tall.
const sigPhoneControl = {
  width: '100%', height: 44, fontSize: 16, padding: '0 36px 0 12px', borderRadius: 10,
  border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit',
  WebkitAppearance: 'none', appearance: 'none',
};
const sigOpenUrl = (url) => {
  if (!url) return;
  if (window.AtlasNative && window.AtlasNative.openExternal) { window.AtlasNative.openExternal(url); return; }
  window.open(url, '_blank', 'noopener');
};

// Inline company search for "Link to existing" — reuses global search and
// keeps only companies. Small on purpose: pick or cancel.
const SignalLinkPicker = ({ initial, onPick, onCancel }) => {
  const [q, setQ] = React.useState(initial || '');
  const [hits, setHits] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); return undefined; }
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => {
      window.AtlasApi.search(term, 15)
        .then((r) => { if (!alive) return; setHits(((r && r.results) || []).filter((x) => x.entity_type === 'company').slice(0, 8)); })
        .catch(() => alive && setHits([]))
        .finally(() => alive && setBusy(false));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return (
    <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies…"
          onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
          style={{ flex: 1, fontSize: 12.5, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
            background: 'var(--surface)', color: 'var(--text)' }} />
        <Btn size="sm" variant="outline" onClick={onCancel}>Cancel</Btn>
      </div>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {busy && hits.length === 0 && <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>Searching…</div>}
        {!busy && q.trim().length >= 2 && hits.length === 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>No companies match. Use “Create company” instead.</div>
        )}
        {hits.map((h) => (
          <button key={h.id} onClick={() => onPick(h)}
            style={{ textAlign: 'left', fontSize: 12.5, padding: '6px 8px', border: 'none', borderRadius: 6,
              background: 'transparent', cursor: 'pointer', color: 'var(--text)' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            {h.title}{h.subtitle ? <span style={{ color: 'var(--text-4)' }}> · {h.subtitle}</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
};

// "Create company" — prefilled from the signal; the person fixes the name
// before it becomes a record. Website only when the signal's URL is the
// company's own site (news hosts are dropped server-side already).
const SignalConvertForm = ({ signal, onDone, onCancel }) => {
  const [name, setName] = React.useState(signal.company_name || '');
  const [website, setWebsite] = React.useState(signal.company_domain || '');
  const [city, setCity] = React.useState(signal.city || '');
  const [state, setState] = React.useState(signal.state || '');
  const [busy, setBusy] = React.useState(false);
  const inp = { fontSize: 12.5, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
    background: 'var(--surface)', color: 'var(--text)' };
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await window.AtlasApi.signalAct(signal.id, 'convert', {
        name: name.trim(), website: website.trim() || null, hq_city: city.trim() || null, hq_state: state.trim() || null,
      });
      pushToast({ type: 'success', title: 'Company created', body: `${name.trim()} · lead source “Web signal”` });
      onDone(r);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not create company', body: e.message });
    } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)',
      display: 'grid', gridTemplateColumns: '2fr 2fr 1.2fr 0.6fr auto auto', gap: 8, alignItems: 'center' }}>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Company name" style={inp}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }} />
      <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="website (optional)" style={inp} />
      <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" style={inp} />
      <input value={state} onChange={(e) => setState(e.target.value)} placeholder="ST" style={inp} maxLength={2} />
      <Btn size="sm" variant="accent" disabled={busy || !name.trim()} onClick={submit}>{busy ? 'Creating…' : 'Create'}</Btn>
      <Btn size="sm" variant="outline" onClick={onCancel} disabled={busy}>Cancel</Btn>
    </div>
  );
};

const SignalRow = ({ s, goToCompany, onChanged }) => {
  const phone = !!window.useDevice().phone;
  const [mode, setMode] = React.useState(null);   // 'link' | 'convert' | null
  const [busy, setBusy] = React.useState(false);
  const act = async (verb, body) => {
    setBusy(true);
    try {
      const r = await window.AtlasApi.signalAct(s.id, verb, body || {});
      onChanged(r);
    } catch (e) {
      pushToast({ type: 'error', title: 'Action failed', body: e.message });
    } finally { setBusy(false); }
  };
  // Into today's Prospecting batch (identify → ownership → qualify runs on it).
  const prospect = async () => {
    setBusy(true);
    try {
      const r = await window.AtlasApi.signalProspect(s.id);
      const p = (r && r.prospecting) || {};
      pushToast({
        type: 'success',
        title: p.added ? `Prospecting · ${p.batch_name}` : `Already in ${p.batch_name || 'the batch'}`,
        body: p.run_started ? 'Identify, ownership and qualify are running.' : undefined,
      });
      onChanged(r);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not prospect', body: e.message });
    } finally { setBusy(false); }
  };
  const place = [s.city, s.state].filter(Boolean).join(', ');
  const who = s.company_name || 'Company not identified';
  const isNew = s.status === 'new';
  const name = s.company_id ? (
    <a href={`#/companies/${s.company_id}`}
      onClick={(e) => { e.preventDefault(); goToCompany && goToCompany(s.company_id); }}
      style={{ fontWeight: 600, color: phone ? 'var(--accent-text)' : 'var(--text)', textDecoration: 'none' }}>
      {s.matched_company_name || who}
    </a>
  ) : (
    <span style={{ fontWeight: 600, color: s.company_name ? 'var(--text)' : 'var(--text-3)' }}>{who}</span>
  );
  // Two different questions, two different pills. Identity: do we know WHICH
  // company (domain = certain, exact name = check the city, blind = the
  // listing names nobody). Relevance: the monitor judge's confidence that the
  // PAGE is about a real for-sale / sold event — it says nothing about who.
  const identity = s.company_id
    ? <Pill hue={s.match_tier === 'domain' || s.match_tier === 'manual' || s.match_tier === 'created' ? 'green' : 'teal'} sz="sm"
        title={s.match_tier === 'domain' ? 'Matched on the company\'s own website — certain'
          : s.match_tier === 'name_exact' ? 'Matched on the exact name only — check the city before acting'
          : 'Linked by a person'}>
        In Atlas{s.match_tier === 'name_exact' ? ' · by name, check the city' : s.match_tier === 'domain' ? ' · by website' : ''}
      </Pill>
    : s.candidate_company_id
      ? <Pill hue="amber" sz="sm">Maybe: {s.candidate_company_name}</Pill>
      : s.company_name
        ? <Pill hue="amber" sz="sm">Not in Atlas</Pill>
        : <Pill hue="slate" sz="sm" title="The listing does not name the business (a blind broker listing). Location and description are all we have.">Blind listing</Pill>;
  const relevance = s.judge_confidence && (
    <span style={{ fontSize: phone ? 13 : 11, color: 'var(--text-4)' }} title="How sure the monitor is that this page is about a real event in the industry — not who the company is">
      relevance {s.judge_confidence}
    </span>
  );
  // Where the row went (acquisition_candidates.route_signal): an "acquired"
  // page about a company Atlas knows is now a record to confirm — on the
  // company and deal pages and under Buyers → Acquisitions; every sale is
  // kept for Nearby; a second article about the same event is folded into
  // the first.
  const routed = (
    <>
      {s.acquisition_id && (
        <Pill hue="amber" sz="sm" title="Filed as an acquisition to confirm — on the company and deal pages, and under Buyers › Acquisitions">
          Acquisitions · to confirm
        </Pill>
      )}
      {!s.acquisition_id && s.market_transaction_id && (
        <Pill hue="slate" sz="sm" title="Kept as a market transaction — the Nearby list and the nearby-sale signal see it">market transaction</Pill>
      )}
      {s.duplicate_of && <Pill hue="slate" sz="sm" title="The same event was reported by an earlier page">repeat</Pill>}
      {(s.prospect_id || s.batch_id) && (
        <a href={`#/prospecting?batch=${s.batch_id || ''}`} style={{ textDecoration: 'none' }} title="Open the Prospecting batch">
          <Pill hue="green" sz="sm">In Prospecting{s.batch_name ? ` · ${s.batch_name}` : ''}</Pill>
        </a>
      )}
    </>
  );
  const what = (
    <>
      {s.summary || s.judge_reason}
      {s.counterparty && (s.event_type === 'acquired' || s.event_type === 'ownership_change') && (
        <span style={{ color: 'var(--text-3)' }}> — {s.counterparty}</span>
      )}
    </>
  );
  const meta = (
    <>
      <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>
        {s.source_host || 'source'}{s.title ? ` · ${s.title.slice(0, 80)}` : ''}
      </a>
      <span>{sigAgo(s.created_at)}</span>
      {s.assignee_name && <span>· for {s.assignee_name}</span>}
      {s.status !== 'new' && <span>· {s.status}{s.reviewer_name ? ` by ${s.reviewer_name}` : ''}{s.review_note ? ` — ${s.review_note}` : ''}</span>}
    </>
  );

  // Phone: the text gets the full width and the actions sit under it. Link…
  // and Create company (a search box, a four-field form) stay on desktop.
  if (phone) {
    const fill = { flex: 1, minWidth: 0, justifyContent: 'center' };
    return (
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', opacity: isNew ? 1 : 0.75 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 15, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
          <Pill hue={SIG_TYPE_HUE[s.event_type] || 'slate'} sz="sm">{SIG_TYPE_LABEL[s.event_type] || 'Signal'}</Pill>
          {name}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {place && <span style={{ fontSize: 14, color: 'var(--text-3)' }}>{place}</span>}
          {s.industry_name && <Pill hue="slate" sz="sm">{s.industry_name}</Pill>}
          {identity}
          {routed}
          {relevance}
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{what}</div>
        <div style={{ fontSize: 13, color: 'var(--text-4)', marginTop: 4, display: 'flex', gap: '2px 8px', flexWrap: 'wrap', overflowWrap: 'anywhere' }}>
          {meta}
        </div>
        {isNew && !s.company_id && s.candidate_company_id && (
          <Btn size="sm" variant="outline" disabled={busy} onClick={() => act('link', { company_id: s.candidate_company_id })}
            style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>It’s {s.candidate_company_name || 'this one'}</span>
          </Btn>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Btn size="sm" variant="outline" icon={<Icon.External size={13} />} onClick={() => sigOpenUrl(s.url)} style={fill}>Open</Btn>
          {canProspectRow(s) && <Btn size="sm" variant="accent" disabled={busy} onClick={prospect} style={fill}>Prospect</Btn>}
          {isNew && <Btn size="sm" variant="outline" disabled={busy} onClick={() => act('review')} style={fill}>Reviewed</Btn>}
          {isNew && <Btn size="sm" variant="ghost" disabled={busy} onClick={() => act('dismiss')} style={fill}>Dismiss</Btn>}
          {!isNew && <Btn size="sm" variant="outline" disabled={busy} onClick={() => act('reopen')} style={fill}>Reopen</Btn>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', opacity: isNew ? 1 : 0.75 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Pill hue={SIG_TYPE_HUE[s.event_type] || 'slate'} sz="sm">{SIG_TYPE_LABEL[s.event_type] || 'Signal'}</Pill>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13 }}>
            {name}
            {place && <span style={{ color: 'var(--text-3)' }}>· {place}</span>}
            {s.industry_name && <Pill hue="slate" sz="sm">{s.industry_name}</Pill>}
            {identity}
            {routed}
            {relevance}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>
            {what}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {meta}
          </div>
          {mode === 'link' && (
            <SignalLinkPicker initial={s.company_name || ''} onCancel={() => setMode(null)}
              onPick={(h) => { setMode(null); act('link', { company_id: h.id }); }} />
          )}
          {mode === 'convert' && (
            <SignalConvertForm signal={s} onCancel={() => setMode(null)}
              onDone={(r) => { setMode(null); onChanged(r); if (r && r.created_company_id && goToCompany) goToCompany(r.created_company_id); }} />
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 260 }}>
          {s.company_id && <Btn size="xs" variant="ghost" onClick={() => goToCompany && goToCompany(s.company_id)}>Open</Btn>}
          {isNew && !s.company_id && s.candidate_company_id && (
            <Btn size="xs" variant="ghost" disabled={busy} onClick={() => act('link', { company_id: s.candidate_company_id })}>
              It’s {String(s.candidate_company_name || 'this one').slice(0, 24)}
            </Btn>
          )}
          {isNew && !s.company_id && <Btn size="xs" variant="ghost" disabled={busy} onClick={() => setMode(mode === 'link' ? null : 'link')}>Link…</Btn>}
          {canProspectRow(s) && (
            <Btn size="xs" variant="accent" disabled={busy} onClick={prospect}
              title="Into today's Prospecting batch — identify, ownership and qualify run on it">Prospect</Btn>
          )}
          {isNew && !s.company_id && <Btn size="xs" variant="ghost" disabled={busy} onClick={() => setMode(mode === 'convert' ? null : 'convert')}>Create company</Btn>}
          {isNew && <Btn size="xs" variant="ghost" disabled={busy} onClick={() => act('review')}>Reviewed</Btn>}
          {isNew && <Btn size="xs" variant="ghost" disabled={busy} onClick={() => act('dismiss')}>Dismiss</Btn>}
          {!isNew && <Btn size="xs" variant="ghost" disabled={busy} onClick={() => act('reopen')}>Reopen</Btn>}
        </div>
      </div>
    </div>
  );
};

const SignalsView = ({ goToCompany, allowed = true }) => {
  // Phone: the top bar already says "Signals".
  const phone = !!window.useDevice().phone;
  if (!allowed) {
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <div style={{ padding: phone ? 16 : '18px 22px', maxWidth: 720 }}>
          {!phone && <h1 style={{ fontSize: 18, fontWeight: 650, margin: 0, color: 'var(--text)' }}>Signals</h1>}
          <div style={{ fontSize: phone ? 15 : 13, color: 'var(--text-3)', marginTop: phone ? 0 : 8, lineHeight: 1.6 }}>
            Signals is turned off for you. An admin can turn it on in Settings → Members (“Signals page”).
          </div>
        </div>
      </div>
    );
  }
  return <SignalsViewBody goToCompany={goToCompany} />;
};

// Two tabs (202609301900): Companies — signals on companies Atlas knows
// (owner timing, went dark, nearby sales / listings, buyer demand), the
// default; Web — what Firecrawl found on the open web (the original page).
const SignalsViewBody = ({ goToCompany }) => {
  const phone = !!window.useDevice().phone;
  // Phone, Back from a record opened here: the page as it was left (_sigLeft).
  const [resume] = React.useState(() => (phone && _sigLeft && _sigLeft.hash === (window.location.hash || '') && _sigLeft.who === sigWho() ? _sigLeft : null));
  React.useEffect(() => { _sigLeft = null; }, []);
  const initialTab = resume ? resume.top : (() => {
    try { return new URLSearchParams((window.location.hash.split('?')[1] || '')).get('tab') || 'companies'; } catch (_) { return 'companies'; }
  })();
  const [top, setTop] = React.useState(initialTab === 'web' ? 'web' : 'companies');
  // #/signals?tab=market opens the Market tab (2026-10-07) — before first paint.
  React.useLayoutEffect(() => { if (initialTab === 'market' && !phone) setTop('market'); }, []);
  const scroller = React.useRef(null);
  const memo = React.useRef(null);
  if (!memo.current) memo.current = { resume, keep: { scrollTop: resume ? resume.scrollTop : 0, web: null, queue: null } };
  const live = React.useRef(null);
  live.current = { phone, top };
  // Leaving for a record, the hash still names this page here: App writes the
  // record's after this cleanup. Back, or a link, changed it already — then
  // there is nothing to come back to.
  React.useEffect(() => () => {
    const r = window.AtlasRouter && window.AtlasRouter.parseHash();
    if (!live.current.phone || !r || r.view !== 'signals') return;
    const { keep } = memo.current;
    const t = live.current.top;
    _sigLeft = { hash: window.location.hash || '', who: sigWho(), top: t, scrollTop: keep.scrollTop,
      web: t === 'web' ? keep.web : null, queue: t === 'companies' ? keep.queue : null };
  }, []);
  // The tab renders its kept rows on this same commit, so the old offset fits.
  React.useLayoutEffect(() => {
    const el = scroller.current;
    const want = resume ? resume.scrollTop : 0;
    if (!el || !want) return;
    el.scrollTop = want;
    if (Math.abs(el.scrollTop - want) > 1) requestAnimationFrame(() => { if (scroller.current) scroller.current.scrollTop = want; });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const keepQueue = React.useCallback((st) => { memo.current.keep.queue = st; }, []);
  // The other tab is a different list: start it from the top, not wherever this one was scrolled to.
  const pickTab = (t) => { setTop(t); memo.current.resume = null; if (scroller.current) scroller.current.scrollTop = 0; };
  const topBtn = (active) => ({
    padding: '0 10px', height: 32, border: 'none', background: 'transparent', fontSize: 12.5, fontWeight: active ? 600 : 500,
    cursor: 'pointer', color: active ? 'var(--text)' : 'var(--text-3)',
    borderBottom: active ? '2px solid var(--text)' : '2px solid transparent', marginBottom: -1,
  });
  const kept = memo.current.resume;
  const tabBody = top === 'companies'
    ? <window.CompanySignalQueue goToCompany={goToCompany} goToDeal={(id) => window.AtlasNav?.goToDeal?.(id)}
        resume={kept ? kept.queue : null} onKeep={phone ? keepQueue : undefined} />
    : top === 'market' && window.MarketView && !phone
      ? <window.MarketView goToCompany={goToCompany} />
      : <WebSignalsBody goToCompany={goToCompany} />;

  // Phone: no heading (the top bar has it); the tab switch spans the width.
  if (phone) {
    return (
      <window.PhoneScroll ref={scroller} onScroll={(e) => { memo.current.keep.scrollTop = e.currentTarget.scrollTop; }}>
        <div style={{ padding: '12px 16px' }}>
          <window.PhoneSegmented value={top} onChange={pickTab}
            options={[{ value: 'companies', label: 'Companies' }, { value: 'web', label: 'Web' }]} />
          <div style={{ marginTop: 12 }}>
            <SigKeep.Provider value={memo.current}>{tabBody}</SigKeep.Provider>
          </div>
        </div>
      </window.PhoneScroll>
    );
  }
  return (
    // The app shell clips its main area; each page scrolls itself.
    <div ref={scroller} style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ padding: '18px 22px 40px', maxWidth: 1180 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 18, fontWeight: 650, margin: 0, color: 'var(--text)' }}>Signals</h1>
        </div>
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginTop: 10 }}>
          <button style={topBtn(top === 'companies')} onClick={() => pickTab('companies')}>Companies</button>
          <button style={topBtn(top === 'web')} onClick={() => pickTab('web')}>Web</button>
          <button style={topBtn(top === 'market')} onClick={() => pickTab('market')}>Market</button>
        </div>
        <div style={{ marginTop: 12 }}>
          {tabBody}
        </div>
      </div>
    </div>
  );
};

const WebSignalsBody = ({ goToCompany }) => {
  const phone = !!window.useDevice().phone;
  // Phone: what this tab starts from on the way Back, and where it keeps its state.
  const memo = React.useContext(SigKeep);
  const [was] = React.useState(() => (memo && memo.resume && memo.resume.web) || null);
  const initialIndustry = was ? was.industry : (() => {
    try { return new URLSearchParams((window.location.hash.split('?')[1] || '')).get('industry') || ''; } catch (_) { return ''; }
  })();
  const [status, setStatus] = React.useState(was ? was.status : 'new');
  const [industry, setIndustry] = React.useState(initialIndustry);
  const [inAtlas, setInAtlas] = React.useState(was ? was.inAtlas : '');          // '' | 'yes' | 'no'
  const [etype, setEtype] = React.useState(was ? was.etype : '');
  const [q, setQ] = React.useState(was ? was.q : '');
  const [summary, setSummary] = React.useState(was ? was.summary : null);
  const [items, setItems] = React.useState(was ? was.items : null);
  const [error, setError] = React.useState(null);
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  const load = React.useCallback(() => {
    const params = { status, limit: WEB_LIMIT };
    if (industry) params.industry_id = industry;
    if (inAtlas) params.in_atlas = inAtlas;
    if (etype) params.event_type = etype;
    if (q.trim()) params.q = q.trim();
    Promise.all([window.AtlasApi.signals(params), window.AtlasApi.signalsSummary()])
      .then(([r, s]) => { setItems(r.items || []); setSummary(s); setError(null); })
      .catch((e) => { setError(e.message); setItems([]); });
  }, [status, industry, inAtlas, etype, q]);
  React.useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);
  React.useEffect(() => {
    if (memo) memo.keep.web = { status, industry, inAtlas, etype, q, items, summary };
  }, [memo, status, industry, inAtlas, etype, q, items, summary]);

  const onChanged = (updated) => {
    // Every action moves the row out of the current tab (except on All).
    setItems((prev) => (prev || []).flatMap((x) => (x.id !== updated.id ? [x] : (status === 'all' || updated.status === status ? [updated] : []))));
    window.AtlasApi.signalsSummary().then(setSummary).catch(() => {});
  };

  // Every loaded row the pipeline can take, in one click.
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const prospectable = (items || []).filter(canProspectRow);
  const prospectAll = async () => {
    const n = prospectable.length;
    const ok = await showConfirm({
      title: `Prospect ${n} ${n === 1 ? 'company' : 'companies'}?`,
      body: 'Each goes into today\'s Prospecting batch; identify, ownership and qualify run on them.',
      confirmLabel: 'Prospect all',
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await window.AtlasApi.signalsProspectMany(prospectable.map((x) => x.id));
      pushToast({
        type: 'success', title: `Prospecting · ${r.batch_name || 'batch'}`,
        body: `${r.added} added${r.duplicates ? `, ${r.duplicates} already there` : ''}${r.skipped && r.skipped.length ? `, ${r.skipped.length} skipped` : ''}.`,
      });
      load();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not prospect', body: e.message });
    } finally { setBulkBusy(false); }
  };

  const counts = (summary && summary.by_status) || {};
  const industries = (summary && summary.by_industry) || [];
  const tabBtn = (active) => ({
    fontSize: 12.5, padding: '5px 10px', borderRadius: 6, border: '1px solid',
    borderColor: active ? 'var(--border-strong)' : 'transparent',
    background: active ? 'var(--surface)' : 'transparent', color: active ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer',
  });
  const sel = { fontSize: 12, padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6,
    background: 'var(--surface)', color: 'var(--text)' };
  const industryOptions = industries.map((i) => ({ value: i.industry_id ? String(i.industry_id) : '', label: i.industry_name || 'No industry', count: i.new }));
  const eventOptions = Object.entries(SIG_TYPE_LABEL).map(([k, l]) => ({ value: k, label: l }));

  const list = (
    <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
      {error && <div style={{ padding: 14, fontSize: phone ? 14 : 12.5, color: 'var(--rose-fg)' }}>{error}</div>}
      {items === null && !error && <div style={{ padding: 14, fontSize: phone ? 14 : 12.5, color: 'var(--text-4)' }}>Loading…</div>}
      {items && items.length === 0 && !error && (
        <div style={{ padding: 18, fontSize: phone ? 14 : 12.5, color: 'var(--text-3)', lineHeight: 1.6 }}>
          {status === 'new' ? 'Nothing new.' : 'Nothing here.'}{' '}
          {summary && !summary.last_signal_at && 'No signals have arrived yet.'}
        </div>
      )}
      {(items || []).map((s) => <SignalRow key={s.id} s={s} goToCompany={goToCompany} onChanged={onChanged} />)}
      {items && items.length >= WEB_LIMIT && (
        <div style={{ padding: '10px 14px', fontSize: phone ? 13 : 12, color: 'var(--text-3)' }}>
          Showing the first {WEB_LIMIT} — narrow it with the filters above.
        </div>
      )}
    </div>
  );

  // Phone: status chips and search stay in view; industry, In Atlas and the
  // event type move into a Filters sheet. The counts the chips don't carry
  // scroll away above them.
  if (phone) {
    const sheetOn = [industry, inAtlas, etype].filter(Boolean).length;
    const field = (label, control) => (
      <div>
        <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>{label}</div>
        {control}
      </div>
    );
    // A native picker here, not window.Picker: in this sheet a Picker would
    // open a second sheet on top of it.
    const select = (value, onChange, options, emptyLabel) => (
      <span style={{ position: 'relative', display: 'block' }}>
        <select value={value} onChange={(e) => onChange(e.target.value)} style={sigPhoneControl}>
          <option value="">{emptyLabel}</option>
          {options.map((o, i) => <option key={o.value + '_' + i} value={o.value}>{o.label}{o.count != null ? ` (${o.count})` : ''}</option>)}
        </select>
        <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-3)', display: 'inline-flex' }}>
          <Icon.ChevronD size={14} />
        </span>
      </span>
    );
    return (
      <div>
        {summary && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, color: 'var(--text-3)', marginBottom: 8 }}>
            <span><b style={{ color: 'var(--amber-fg)' }}>{summary.new_not_in_atlas || 0}</b> not in Atlas</span>
            <span>{summary.last_signal_at ? `last signal ${sigAgo(summary.last_signal_at)}` : 'nothing received yet'}</span>
          </div>
        )}
        {/* Edge to edge: out over the page's 16px gutter. */}
        <div style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg)', margin: '0 -16px', padding: '8px 0 10px', borderBottom: '1px solid var(--border)' }}>
          <window.PhoneChips value={status} onChange={setStatus}
            options={SIG_STATUS_TABS.map(([k, label]) => ({ value: k, label, count: k !== 'all' && counts[k] ? counts[k] : null }))} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 16px', marginTop: 10 }}>
            <window.PhoneSearchField value={q} onChange={setQ} placeholder="Search" style={{ flex: 1, minWidth: 0 }} />
            <window.PhoneFilterButton count={sheetOn} onClick={() => setFiltersOpen(true)} />
          </div>
        </div>
        {list}
        <window.PhoneSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters">
          <div style={{ padding: '4px 18px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {field('Industry', select(industry, setIndustry, industryOptions, 'All industries'))}
            {field('In Atlas', (
              <window.PhoneSegmented value={inAtlas} onChange={setInAtlas}
                options={[{ value: '', label: 'Either' }, { value: 'no', label: 'Not in Atlas' }, { value: 'yes', label: 'In Atlas' }]} />
            ))}
            {field('Event', select(etype, setEtype, eventOptions, 'Any event'))}
          </div>
        </window.PhoneSheet>
      </div>
    );
  }

  return (
    <div>
      {/* The filters stay in view while the list scrolls under them. */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg)', padding: '8px 0 10px', borderBottom: '1px solid var(--border)' }}>
        {summary && (
          <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-3)', flexWrap: 'wrap' }}>
            <span><b style={{ color: 'var(--text)' }}>{counts.new || 0}</b> new</span>
            <span><b style={{ color: 'var(--amber-fg)' }}>{summary.new_not_in_atlas || 0}</b> not in Atlas</span>
            <span>{counts.converted || 0} converted · {counts.reviewed || 0} reviewed · {counts.dismissed || 0} dismissed</span>
            {summary.last_signal_at ? <span>· last signal {sigAgo(summary.last_signal_at)}</span> : <span>· nothing received yet</span>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: summary ? 10 : 0, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--surface-2)', borderRadius: 8 }}>
            {SIG_STATUS_TABS.map(([k, label]) => (
              <button key={k} style={tabBtn(status === k)} onClick={() => setStatus(k)}>
                {label}{k !== 'all' && counts[k] ? ` ${counts[k]}` : ''}
              </button>
            ))}
          </div>
          <window.Picker value={industry} onChange={setIndustry} options={industryOptions} emptyLabel="All industries" placeholder="Industry" minWidth={160} />
          <window.Picker value={inAtlas} onChange={setInAtlas} emptyLabel="In Atlas or not" placeholder="In Atlas"
            options={[{ value: 'no', label: 'Not in Atlas' }, { value: 'yes', label: 'In Atlas' }]} />
          <window.Picker value={etype} onChange={setEtype} options={eventOptions} emptyLabel="Any event" placeholder="Event" minWidth={140} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search signals…"
            style={{ ...sel, minWidth: 180 }} />
          {prospectable.length > 0 && (
            <Btn size="sm" variant="accent" disabled={bulkBusy} onClick={prospectAll} style={{ marginLeft: 'auto' }}
              title="Every loaded row about a company Atlas does not know goes into today's Prospecting batch">
              {bulkBusy ? 'Prospecting…' : `Prospect all ${prospectable.length}`}
            </Btn>
          )}
        </div>
      </div>

      {list}
    </div>
  );
};

window.SignalsView = SignalsView;
